import * as _ from 'underscore'

import { ColumnModel } from '../../modules/api/api.models'

import { OperatorLayoutSettings, LocalizationHelper, TypeHelper, Dictionary } from '../../helpers'

import { Enums, Activity } from '../../models/common'
import { OpOperator, OpDistributor } from '../../models/core'
import { LocationOptions } from '../../models/util/sms-notification-options'
import { IssueOptions, NotificationType, TaskOptions, WorkOrderOptions } from '../../models/util/email-notification-options'

import { IMRTabsState } from '../../modules/common/tabs'

import { REFERENCE_TYPE_SERVICE_NAME, ReferenceTypeService } from '../reference-type'
import { USER_SESSION_SERVICE_NAME, UserSessionService } from '../user-session'
import { IReferenceType } from '../../models/util/interfaces'
import { OPERATOR_SERVICE_NAME, OperatorService } from './operator.service'


//#region Consts

export const OPERATOR_NOTIFICATIONS_SERVICE_NAME: string = 'OperatorNotificationsService'
export const OPERATOR_NOTIFICATIONS_SERVICE_PROVIDER_NAME: string = 'OperatorNotificationsServiceProvider'

//#endregion

export class OperatorNotificationsService {

    //#region Ctor

    constructor(
        private operatorService: OperatorService,
        private referenceTypeService: ReferenceTypeService,
        private userSessionService: UserSessionService
    ) { }

    //#endregion


    //#region Columns

    public getNotificationsColumns(isEditable: boolean = false): ColumnModel[] {
        const columns: ColumnModel[] = []
        const actionColumn = new ColumnModel()
        const notificationByEmailColumn = new ColumnModel()
        const notificationBySmsColumn = new ColumnModel()

        actionColumn.idName = 'action'
        actionColumn.type = Enums.DataTypeClass.Text
        actionColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Action)

        columns.push(actionColumn)

        if (this.userSessionService.getLoggedOperator().hasPermission(Activity.OPERATOR_NOTIFICATIONS_LIST)) {
            notificationByEmailColumn.idName = 'notificationByEmail'
            notificationByEmailColumn.type = Enums.DataTypeClass.Boolean
            notificationByEmailColumn.allowEditing = isEditable
            notificationByEmailColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NotificationByEmail, {
                collection: 'Notification'
            })

            columns.push(notificationByEmailColumn)
        }

        if (this.userSessionService.getLoggedOperator().hasPermission(Activity.OPERATOR_SMS_NOTIFICATIONS_LIST)) {
            notificationBySmsColumn.idName = 'notificationBySms'
            notificationBySmsColumn.type = Enums.DataTypeClass.Boolean
            notificationBySmsColumn.allowEditing = isEditable
            notificationBySmsColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NotificationBySms, {
                collection: 'Notification'
            })

            columns.push(notificationBySmsColumn)
        }

        return columns
    }

    //#endregion


    //#region Grid

    public createNotificationGridDataSource(operator: OpOperator, notificationType: NotificationType): any[] {
        if (operator == null || notificationType == null) {
            return []
        }

        const dataSource: any[] = []
        const currentEnum: typeof IssueOptions | typeof TaskOptions | typeof WorkOrderOptions | typeof LocationOptions = this.getEnumFromType(notificationType)

        if (currentEnum != null) {
            TypeHelper.iterateEnum(currentEnum, (value: any, name: string) => {
                dataSource.push({
                    action: LocalizationHelper.getLocalizedText(name, {
                        collection: 'Notification'
                    }),
                    id: value,
                    notificationByEmail: this.isEmailOptionSet(operator, notificationType, value),
                    notificationBySms: this.isSmsOptionSet(operator, notificationType, value),
                })
            })
        }

        return dataSource
    }

    public updateOptions(operator: OpOperator, notificationType: NotificationType, key: any, newData: any) {
        if (newData != null) {
            if (newData.notificationByEmail != null) {
                if (newData.notificationByEmail) {
                    this.setEmailNotify(operator, notificationType, key.id)
                }
                else {
                    this.resetEmailNotify(operator, notificationType, key.id)
                }
            }
            else if (newData.notificationBySms != null) {
                if (newData.notificationBySms) {
                    this.setSmsNotify(operator, notificationType, key.id)
                }
                else {
                    this.resetSmsNotify(operator, notificationType, key.id)
                }
            }
        }
    }

    //#endregion


    //#region Distributors

    public getAvailableNotificationDistributors(): JQueryPromise<OpDistributor[]> {
        return this.userSessionService
            .getDataProvider().System
            .getAllowedList([Enums.ReferenceType.IdDistributor])
            .then((dicts: Dictionary<Dictionary<IReferenceType>>) => {
                const distributorDict: Dictionary<OpDistributor> = this.referenceTypeService.extractReferenceTypeDictionary<OpDistributor>(dicts, Enums.ReferenceType.IdDistributor, (item: OpDistributor) => item.getReferenceKey())

                if (distributorDict != null) {
                    return distributorDict.values()
                }

                return []
            })
    }

    public getIdDistributorsFromNotification(operator: OpOperator, type: NotificationType): number[] {
        if (operator != null) {
            switch (type) {
                case NotificationType.Issue:
                    return operator.emailNotificationOptions.getIssueIdDistributorOptions()

                case NotificationType.Task:
                    return operator.emailNotificationOptions.getTaskIdDistributorOptions()

                case NotificationType.Location:
                    return operator.emailNotificationOptions.getLocationIdDistributorOptions()
            }
        }

        return []
    }

    public updateNotificationDistributors(operator: OpOperator, distributors: OpDistributor[], type: NotificationType): void {
        if (operator != null && distributors != null) {
            operator.emailNotificationOptions.setNotify(distributors, type)
            operator.smsNotificationOptions.setNotify(distributors, type)
        }
    }

    //#endregion


    //#region Notifications

    private isEmailOptionSet(operator: OpOperator, type: NotificationType, option: IssueOptions | TaskOptions | WorkOrderOptions | LocationOptions): boolean {
        switch (type) {
            case NotificationType.Issue:
                return operator.emailNotificationOptions.getIssueNotify(option as IssueOptions)

            case NotificationType.Task:
                return operator.emailNotificationOptions.getTaskNotify(option as TaskOptions)

            case NotificationType.WorkOrder:
                return operator.emailNotificationOptions.getWorkOrderNotify(option as WorkOrderOptions)

            case NotificationType.Location:
                return operator.emailNotificationOptions.getLocationNotify(option as LocationOptions)
        }

        return false
    }

    private isSmsOptionSet(operator: OpOperator, type: NotificationType, option: IssueOptions | TaskOptions | WorkOrderOptions | LocationOptions): boolean {
        switch (type) {
            case NotificationType.Issue:
                return operator.smsNotificationOptions.getIssueNotify(option as IssueOptions)

            case NotificationType.Task:
                return operator.smsNotificationOptions.getTaskNotify(option as TaskOptions)

            case NotificationType.WorkOrder:
                return operator.smsNotificationOptions.getWorkOrderNotify(option as WorkOrderOptions)

            case NotificationType.Location:
                return operator.smsNotificationOptions.getLocationNotify(option as LocationOptions)
        }

        return false
    }

    private getEnumFromType(type: NotificationType): typeof IssueOptions | typeof TaskOptions | typeof WorkOrderOptions | typeof LocationOptions {
        switch (type) {
            case NotificationType.Issue:
                return IssueOptions

            case NotificationType.Task:
                return TaskOptions

            case NotificationType.WorkOrder:
                return WorkOrderOptions

            case NotificationType.Location:
                return LocationOptions
        }

        return null
    }

    private setEmailNotify(operator: OpOperator, type: NotificationType, option: IssueOptions | TaskOptions | WorkOrderOptions | LocationOptions) {
        switch (type) {
            case NotificationType.Issue:
                operator.emailNotificationOptions.setIssueNotify(option as IssueOptions)

                break

            case NotificationType.Task:
                operator.emailNotificationOptions.setTaskNotify(option as TaskOptions)

                break

            case NotificationType.WorkOrder:
                operator.emailNotificationOptions.setWorkOrderNotify(option as WorkOrderOptions)

                break

            case NotificationType.Location:
                operator.emailNotificationOptions.setLocationNotify(option as LocationOptions)

                break
        }
    }

    private setSmsNotify(operator: OpOperator, type: NotificationType, option: IssueOptions | TaskOptions | WorkOrderOptions | LocationOptions) {
        switch (type) {
            case NotificationType.Issue:
                operator.smsNotificationOptions.setIssueNotify(option as IssueOptions)

                break

            case NotificationType.Task:
                operator.smsNotificationOptions.setTaskNotify(option as TaskOptions)

                break

            case NotificationType.WorkOrder:
                operator.smsNotificationOptions.setWorkOrderNotify(option as WorkOrderOptions)

                break

            case NotificationType.Location:
                operator.smsNotificationOptions.setLocationNotify(option as LocationOptions)

                break
        }
    }

    private resetEmailNotify(operator: OpOperator, type: NotificationType, option: IssueOptions | TaskOptions | WorkOrderOptions | LocationOptions) {
        switch (type) {
            case NotificationType.Issue:
                operator.emailNotificationOptions.resetIssueNotify(option as IssueOptions)

                break

            case NotificationType.Task:
                operator.emailNotificationOptions.resetTaskNotify(option as TaskOptions)

                break

            case NotificationType.WorkOrder:
                operator.emailNotificationOptions.resetWorkOrderNotify(option as WorkOrderOptions)

                break

            case NotificationType.Location:
                operator.emailNotificationOptions.resetLocationNotify(option as LocationOptions)

                break
        }
    }

    private resetSmsNotify(operator: OpOperator, type: NotificationType, option: IssueOptions | TaskOptions | WorkOrderOptions | LocationOptions) {
        switch (type) {
            case NotificationType.Issue:
                operator.smsNotificationOptions.resetIssueNotify(option as IssueOptions)

                break

            case NotificationType.Task:
                operator.smsNotificationOptions.resetTaskNotify(option as TaskOptions)

                break

            case NotificationType.WorkOrder:
                operator.smsNotificationOptions.resetWorkOrderNotify(option as WorkOrderOptions)

                break

            case NotificationType.Location:
                operator.smsNotificationOptions.resetLocationNotify(option as LocationOptions)

                break
        }
    }

    //#endregion


    //#region Save

    public saveNotificationsToOperator(sourceOperator: OpOperator, operatorWithNotifications: OpOperator): JQueryPromise<OpOperator> {
        const operatorToSave: OpOperator = sourceOperator.clone()

        operatorToSave.opState = Enums.OpChangeState.Modified

        if (sourceOperator.actor != null) {
            operatorToSave.actor = sourceOperator.actor.clone()
            operatorToSave.actor.avatar = null
        }

        operatorToSave.dataList = operatorWithNotifications.dataList
        operatorToSave.emailNotificationOptions = operatorWithNotifications.emailNotificationOptions
        operatorToSave.smsNotificationOptions = operatorWithNotifications.smsNotificationOptions

        // Circular reference
        operatorToSave.emailNotificationOptions.owner = null
        operatorToSave.smsNotificationOptions.owner = null

        return this.operatorService.saveOperator(operatorToSave)
    }

    //#endregion


    //#region Tabs

    public restoreTabState(): IMRTabsState {
        return {
            selectedTabIndex: OperatorLayoutSettings.getSetting<number>(OperatorLayoutSettings.UserPopupNotificationsTabActive)
        }
    }

    public saveTabState(state: IMRTabsState) {
        OperatorLayoutSettings.setSetting(OperatorLayoutSettings.UserPopupNotificationsTabActive, state.selectedTabIndex)
    }

    //#endregion
}

export class OperatorNotificationserviceProvider implements ng.IServiceProvider {

    //#region IServiceProvider Implementation 

    public $get = [
        OPERATOR_SERVICE_NAME,
        REFERENCE_TYPE_SERVICE_NAME,
        USER_SESSION_SERVICE_NAME,
        function (
            operatorService: OperatorService,
            referenceTypeService: ReferenceTypeService,
            userSessionService: UserSessionService
        ) {
            return new OperatorNotificationsService(operatorService, referenceTypeService, userSessionService)
        }]

    //#endregion
} 